import { Address, getAddressFromPublicKey } from '@wiko/addresses';
import { Decoder } from '@wiko/codecs-core';
import { getBase58Decoder } from '@wiko/codecs-strings';
import {
    WIKO_ERROR__TRANSACTION__ADDRESSES_CANNOT_SIGN_TRANSACTION,
    WIKO_ERROR__TRANSACTION__FEE_PAYER_SIGNATURE_MISSING,
    WIKO_ERROR__TRANSACTION__SIGNATURES_MISSING,
    WikoError,
} from '@wiko/errors';
import { Signature, SignatureBytes, signBytes } from '@wiko/keys';

import { Transaction } from './transaction';

export interface FullySignedTransaction extends Transaction {
    readonly __brand: unique symbol;
}

let base58Decoder: Decoder<string> | undefined;

export function getSignatureFromTransaction(transaction: Transaction): Signature {
    if (!base58Decoder) base58Decoder = getBase58Decoder();

    // We have ordered signatures from the compiled message accounts
    // first signature is the fee payer
    const signatureBytes = Object.values(transaction.signatures)[0];
    if (!signatureBytes) {
        throw new WikoError(WIKO_ERROR__TRANSACTION__FEE_PAYER_SIGNATURE_MISSING);
    }
    const transactionSignature = base58Decoder.decode(signatureBytes);
    return transactionSignature as Signature;
}

function uint8ArraysEqual(arr1: Uint8Array, arr2: Uint8Array) {
    return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
}

export async function partiallySignTransaction<T extends Transaction>(
    keyPairs: CryptoKeyPair[],
    transaction: T,
): Promise<T> {
    let newSignatures: Record<Address, SignatureBytes> | undefined;
    let unexpectedSigners: Set<Address> | undefined;

    await Promise.all(
        keyPairs.map(async keyPair => {
            const address = await getAddressFromPublicKey(keyPair.publicKey);
            const existingSignature = transaction.signatures[address];

            // Check if the address is expected to sign the transaction
            if (existingSignature === undefined) {
                // address is not an expected signer for this transaction
                unexpectedSigners ||= new Set();
                unexpectedSigners.add(address);
                return;
            }

            // Return if there are any unexpected signers already since we won't be using signatures
            if (unexpectedSigners) {
                return;
            }

            const newSignature = await signBytes(keyPair.privateKey, transaction.messageBytes);

            if (existingSignature !== null && uint8ArraysEqual(newSignature, existingSignature)) {
                // already have the same signature set
                return;
            }

            newSignatures ||= {};
            newSignatures[address] = newSignature;
        }),
    );

    if (unexpectedSigners && unexpectedSigners.size > 0) {
        const expectedSigners = Object.keys(transaction.signatures);
        throw new WikoError(WIKO_ERROR__TRANSACTION__ADDRESSES_CANNOT_SIGN_TRANSACTION, {
            expectedAddresses: expectedSigners,
            unexpectedAddresses: [...unexpectedSigners],
        });
    }

    if (!newSignatures) {
        return transaction;
    }

    return Object.freeze({
        ...transaction,
        signatures: Object.freeze({
            ...transaction.signatures,
            ...newSignatures,
        }),
    });
}

export async function signTransaction<T extends Transaction>(
    keyPairs: CryptoKeyPair[],
    transaction: T,
): Promise<FullySignedTransaction & T> {
    const out = await partiallySignTransaction(keyPairs, transaction);
    assertTransactionIsFullySigned(out);
    Object.freeze(out);
    return out;
}

export function assertTransactionIsFullySigned(
    transaction: Transaction,
): asserts transaction is FullySignedTransaction {
    const missingSigs: Address[] = [];
    Object.entries(transaction.signatures).forEach(([address, signatureBytes]) => {
        if (!signatureBytes) {
            missingSigs.push(address as Address);
        }
    });

    if (missingSigs.length > 0) {
        throw new WikoError(WIKO_ERROR__TRANSACTION__SIGNATURES_MISSING, {
            addresses: missingSigs,
        });
    }
}
